import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, GoogleAuthProvider } from 'firebase/auth';
import {
  clearIndexedDbPersistence,
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  terminate,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

/**
 * Internal Firebase init — this file and its siblings in `shared/api/` are the
 * ONLY modules allowed to import `firebase/*` (ADR-0003, enforced by review /
 * the boundary check). Components and feature hooks import the typed adapter
 * functions instead.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // measurementId is only used by Analytics, which we do NOT initialize by
  // default — this app renders call transcripts (PII), so analytics/tracking
  // must sit behind an EU cookie-consent gate (HLD §6, doc 04 GDPR).
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
/**
 * Firestore with the persistent (IndexedDB) cache, shared across tabs.
 *
 * With the default memory cache every listener started from nothing: a
 * dashboard opened cold, or paged to another week, showed no bookings until the
 * server answered that exact query — and when the connection was slow to
 * answer, the SDK waits out its 10-second online-state timeout before it will
 * serve anything at all. That was the "up to 10 seconds" on the dashboard. With
 * a persistent cache, anything seen before is drawn immediately from disk and
 * the live listener corrects it a moment later.
 *
 * The cache holds the owner's own data on the owner's own device, next to the
 * auth session that already grants access to all of it; it is wiped on sign-out
 * (`clearLocalData`) so a shared computer does not keep it after the session.
 * Where IndexedDB is unavailable (some private modes) the SDK falls back to the
 * memory cache by itself.
 */
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
export const functions = getFunctions(app, import.meta.env.VITE_FUNCTIONS_REGION || 'europe-west3');
export const storage = getStorage(app);

/** Google sign-in; email/password is the other provider (doc 88, `auth.ts`). */
export const googleProvider = new GoogleAuthProvider();

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectStorageEmulator(storage, 'localhost', 9199);
}

/** Set while a cache wipe is owed; cleared once one succeeds. */
const CLEAR_PENDING = 'tc24.clearFirestoreCache';

/**
 * Drops the on-device Firestore cache after sign-out. The instance has to be
 * terminated first and is unusable afterwards, so the caller reloads the page.
 *
 * The wipe is refused while any other tab still has the cache open. Every tab
 * runs this on sign-out (see `auth.ts`), so the last tab to let go normally
 * succeeds — and for the case where none does, the flag below makes the next
 * page load retry before the cache is opened again. Never throws: signing out
 * must not fail because a cache could not be deleted yet.
 */
export async function clearLocalData(): Promise<void> {
  writeFlag(true);
  try {
    await terminate(db);
    await clearIndexedDbPersistence(db);
    writeFlag(false);
  } catch {
    /* another tab still holds the cache — retried on the next load */
  }
}

// A wipe owed by an earlier sign-out runs before anything touches `db`: the
// SDK queues it ahead of the persistence start, so it never races a listener.
if (readFlag()) {
  clearIndexedDbPersistence(db).then(
    () => writeFlag(false),
    () => {
      /* still held by another tab — the flag stays for the next load */
    },
  );
}

function readFlag(): boolean {
  try {
    return localStorage.getItem(CLEAR_PENDING) === '1';
  } catch {
    return false;
  }
}

function writeFlag(on: boolean): void {
  try {
    if (on) localStorage.setItem(CLEAR_PENDING, '1');
    else localStorage.removeItem(CLEAR_PENDING);
  } catch {
    /* no storage — nothing to retry from, and nothing was cached there either */
  }
}
